'use strict';

var uint8arrayExtras = require('uint8array-extras');
var util = require('./util/util.js');
require('../xdr/index.js');
var keypair = require('./keypair.js');
var strkey = require('./strkey.js');
var hashing = require('./hashing.js');
var asset = require('../xdr/generated/asset.js');
var changeTrustAsset = require('../xdr/generated/change-trust-asset.js');
var trustLineAsset = require('../xdr/generated/trust-line-asset.js');
var hashIdPreimage = require('../xdr/generated/hash-id-preimage.js');
var hashIdPreimageContractId = require('../xdr/generated/hash-id-preimage-contract-id.js');
var contractIdPreimage = require('../xdr/generated/contract-id-preimage.js');
var alphaNum4 = require('../xdr/generated/alpha-num4.js');
var alphaNum12 = require('../xdr/generated/alpha-num12.js');
var assetType = require('../xdr/generated/asset-type.js');

const AssetType = {
  native: "native",
  credit4: "credit_alphanum4",
  credit12: "credit_alphanum12",
  liquidityPoolShares: "liquidity_pool_shares"
};
function asciiCompare(a, b) {
  return uint8arrayExtras.compareUint8Arrays(uint8arrayExtras.stringToUint8Array(a), uint8arrayExtras.stringToUint8Array(b));
}
class Asset {
  /** The asset code. */
  code;
  /** The account ID of the issuer. Undefined for the native asset. */
  issuer;
  /**
   * @param code - The asset code.
   * @param issuer - The account ID of the issuer.
   */
  constructor(code, issuer) {
    if (!/^[a-zA-Z0-9]{1,12}$/.test(code)) {
      throw new Error(
        "Asset code is invalid (maximum alphanumeric, 12 characters at max)"
      );
    }
    if (String(code).toLowerCase() !== "xlm" && !issuer) {
      throw new Error("Issuer cannot be null");
    }
    if (issuer && !strkey.StrKey.isValidEd25519PublicKey(issuer)) {
      throw new Error("Issuer is invalid");
    }
    if (String(code).toLowerCase() === "xlm") {
      this.code = "XLM";
    } else {
      this.code = code;
    }
    this.issuer = issuer;
  }
  /**
   * Returns an asset object for the native asset.
   */
  static native() {
    return new Asset("XLM");
  }
  /**
   * Returns an asset object from its XDR object representation.
   * @param assetXdr - The asset xdr object.
   */
  static fromOperation(assetXdr) {
    let anum;
    let code;
    let issuer;
    switch (assetXdr.type) {
      case "assetTypeNative":
        return this.native();
      case "assetTypeCreditAlphanum4":
        anum = assetXdr.alphaNum4;
        issuer = strkey.StrKey.encodeEd25519PublicKey(anum.issuer.ed25519.toBytes());
        code = util.trimEnd(
          uint8arrayExtras.uint8ArrayToString(anum.assetCode.toBytes()),
          "\0"
        );
        return new this(code, issuer);
      case "assetTypeCreditAlphanum12":
        anum = assetXdr.alphaNum12;
        issuer = strkey.StrKey.encodeEd25519PublicKey(anum.issuer.ed25519.toBytes());
        code = util.trimEnd(
          uint8arrayExtras.uint8ArrayToString(anum.assetCode.toBytes()),
          "\0"
        );
        return new this(code, issuer);
      default:
        throw new Error("Invalid asset type received: " + assetXdr.type);
    }
  }
  /**
   * Returns the xdr.Asset object for this asset.
   */
  toXdrObject() {
    return this._toXdrObject(asset.Asset);
  }
  /**
   * Returns the xdr.ChangeTrustAsset object for this asset.
   */
  toChangeTrustXdrObject() {
    return this._toXdrObject(changeTrustAsset.ChangeTrustAsset);
  }
  /**
   * Returns the xdr.TrustLineAsset object for this asset.
   */
  toTrustLineXdrObject() {
    return this._toXdrObject(trustLineAsset.TrustLineAsset);
  }
  /**
   * @deprecated Use {@link toXdrObject} instead.
   * Deprecated in version v17.0.0
   */
  toXDRObject() {
    return this.toXdrObject();
  }
  /**
   * @deprecated Use {@link toChangeTrustXdrObject} instead.
   * Deprecated in version v17.0.0
   */
  toChangeTrustXDRObject() {
    return this.toChangeTrustXdrObject();
  }
  /**
   * @deprecated Use {@link toTrustLineXdrObject} instead.
   * Deprecated in version v17.0.0
   */
  toTrustLineXDRObject() {
    return this.toTrustLineXdrObject();
  }
  /**
   * Returns the would-be contract ID (`C...` format) for this asset on a given
   * network.
   *
   * @param networkPassphrase - indicates which network the contract
   *    ID should refer to, since every network will have a unique ID for the
   *    same contract (see {@link Networks} for options)
   *
   * @remarks
   * **Warning:** This makes no guarantee that this contract actually *exists*.
   */
  contractId(networkPassphrase) {
    const networkId = hashing.hash(uint8arrayExtras.stringToUint8Array(networkPassphrase));
    const preimage = hashIdPreimage.HashIdPreimage.envelopeTypeContractId(
      new hashIdPreimageContractId.HashIdPreimageContractId({
        networkId,
        contractIdPreimage: contractIdPreimage.ContractIdPreimage.contractIdPreimageFromAsset(
          this.toXdrObject()
        )
      })
    );
    return strkey.StrKey.encodeContract(hashing.hash(preimage.toXdr()));
  }
  /**
   * Returns the xdr object for this asset.
   * @param xdrAsset - The xdr asset constructor.
   */
  _toXdrObject(xdrAsset) {
    if (this.isNative()) {
      return xdrAsset.assetTypeNative();
    }
    if (!this.issuer) {
      throw new Error("Issuer cannot be null for non-native asset");
    }
    if (this.code.length <= 4) {
      const assetType2 = new alphaNum4.AlphaNum4({
        assetCode: uint8arrayExtras.stringToUint8Array(this.code.padEnd(4, "\0")),
        issuer: keypair.Keypair.fromPublicKey(this.issuer).xdrAccountId()
      });
      return xdrAsset.assetTypeCreditAlphanum4(assetType2);
    }
    const assetType = new alphaNum12.AlphaNum12({
      assetCode: uint8arrayExtras.stringToUint8Array(this.code.padEnd(12, "\0")),
      issuer: keypair.Keypair.fromPublicKey(this.issuer).xdrAccountId()
    });
    return xdrAsset.assetTypeCreditAlphanum12(assetType);
  }
  /**
   * Returns the asset code
   */
  getCode() {
    return String(this.code);
  }
  /**
   * Returns the asset issuer
   */
  getIssuer() {
    if (this.issuer === void 0) {
      return void 0;
    }
    return String(this.issuer);
  }
  /**
   * @see [Assets concept](https://developers.stellar.org/docs/glossary/assets/)
   * Returns the asset type. Can be one of following types:
   *
   *  - `native`,
   *  - `credit_alphanum4`,
   *  - `credit_alphanum12`
   * @throws if asset type is unsupported.
   */
  getAssetType() {
    switch (this.getRawAssetType().value) {
      case assetType.AssetType.assetTypeNative.value:
        return AssetType.native;
      case assetType.AssetType.assetTypeCreditAlphanum4.value:
        return AssetType.credit4;
      case assetType.AssetType.assetTypeCreditAlphanum12.value:
        return AssetType.credit12;
      default:
        throw new Error(
          "Supported asset types are: native, credit_alphanum4, credit_alphanum12. received: " + this.getRawAssetType().name
        );
    }
  }
  /**
   * Returns the raw XDR representation of the asset type
   */
  getRawAssetType() {
    if (this.isNative()) {
      return assetType.AssetType.assetTypeNative;
    }
    if (this.code.length <= 4) {
      return assetType.AssetType.assetTypeCreditAlphanum4;
    }
    return assetType.AssetType.assetTypeCreditAlphanum12;
  }
  /**
   * Returns true if this asset object is the native asset.
   */
  isNative() {
    return !this.issuer;
  }
  /**
   * Returns true if this asset equals the given asset.
   *
   * @param asset - Asset to compare
   */
  equals(asset) {
    return this.code === asset.getCode() && this.issuer === asset.getIssuer();
  }
  /**
   * Returns a string representation of this asset.
   *
   * Native assets return `"native"`. Non-native assets return `"code:issuer"`.
   */
  toString() {
    if (this.isNative()) {
      return "native";
    }
    return `${this.getCode()}:${this.getIssuer()}`;
  }
  /**
   * Compares two assets according to the criteria:
   *
   *  1. First compare the type (`native < alphanum4 < alphanum12`).
   *  2. If the types are equal, compare the assets codes.
   *  3. If the asset codes are equal, compare the issuers.
   *
   * @param assetA - the first asset
   * @param assetB - the second asset
   */
  static compare(assetA, assetB) {
    if (!assetA || !(assetA instanceof Asset)) {
      throw new Error("assetA is invalid");
    }
    if (!assetB || !(assetB instanceof Asset)) {
      throw new Error("assetB is invalid");
    }
    if (assetA.equals(assetB)) {
      return 0;
    }
    const xdrAtype = assetA.getRawAssetType().value;
    const xdrBtype = assetB.getRawAssetType().value;
    if (xdrAtype !== xdrBtype) {
      return xdrAtype < xdrBtype ? -1 : 1;
    }
    const result = asciiCompare(assetA.getCode(), assetB.getCode());
    if (result !== 0) {
      return result;
    }
    const issuerA = assetA.getIssuer();
    const issuerB = assetB.getIssuer();
    if (issuerA === void 0 || issuerB === void 0) {
      throw new Error("Issuer is undefined for non-native asset");
    }
    return asciiCompare(issuerA, issuerB);
  }
}

exports.Asset = Asset;
exports.AssetType = AssetType;
//# sourceMappingURL=asset.js.map
